import React, { useState } from 'react';
import { Mountain, Compass, FileCode2, Map, Building2, ArrowRight, Check, X, Droplets } from 'lucide-react';

const servicesData = [
  {
    id: 'dgps',
    icon: Compass,
    image: '/foif_a90_field.jpg',
    title: 'High Accuracy Land Survey by Using DGPS',
    category: 'Satellite Surveying',
    shortDesc: 'Real-Time Kinematic (RTK) dual-frequency GNSS coordinate locking for millimeter precision land boundaries.',
    longDesc: 'Our DGPS (Differential Global Positioning System) survey leverages dual-frequency GNSS technology tied to official satellite constellations. Provides millimeter-level spatial accuracy for property boundaries, infrastructure, and WGS84/UTM geo-referencing.',
    features: [
      'Dual-frequency L1/L2 RTK satellite positioning',
      'Real-time WGS84 and UTM grid coordinate fixing',
      'Fast boundary monumentation & corner pin setting',
      'High-speed coverage for large acreage and plots',
      'Village survey map overlay & title verification'
    ],
    deliverables: ['UTM & WGS84 Coordinate Master Table', 'DGPS Georeferenced Boundary Map', 'Corner Pillar Layout Plan', 'Cadastral Overlay File'],
    instruments: 'FOIF A90 Dual-Frequency RTK DGPS / GNSS Receivers, Base & Rover System'
  },
  {
    id: 'wrd',
    icon: Droplets,
    image: '/surveyor_water_work.jpg',
    title: 'Water Resources Development (W.R.D Work)',
    category: 'Hydraulic & Water Engineering',
    shortDesc: 'Specialized water resources survey, canal cross-sections, tank bund profiling, dam site mapping, and drainage channels.',
    longDesc: 'Expert surveying for Water Resources Department (W.R.D.) projects including canal alignment, river basin contours, reservoir capacity calculation, drainage channel gradients, and embankment profiling.',
    features: [
      'Canal & river bed cross-section profiling',
      'Water tank bund & sluice level surveying',
      'Catchment area & watershed elevation contouring',
      'Embankment cut/fill volume calculation',
      'Flood line & discharge gradient mapping'
    ],
    deliverables: ['Canal L-Section & Cross-Section CAD Sheets', 'Reservoir Capacity Surface Model', 'W.R.D Format Survey Report', 'HFL (High Flood Level) Contour Map'],
    instruments: 'Leica Total Station, Auto Level, RTK DGPS, Bathymetric Echo Sounders'
  },
  {
    id: 'topographical',
    icon: Mountain,
    image: '/survey_topo.png',
    title: 'Topographical & Contour Surveys',
    category: 'Engineering & Terrain',
    shortDesc: 'Comprehensive ground elevation mapping, MSL bench-marking, slope analysis, and 2D contour generation.',
    longDesc: 'Captures the complete physical footprint and elevation profile of land parcels. Establishes exact Mean Sea Level (MSL) benchmark tying for architects, civil engineers, and site planners.',
    features: [
      'Digital Terrain & Elevation Models (DTM/DEM)',
      'High-precision MSL Benchmark Station Tying',
      'Undulation, slope, and drainage pattern mapping',
      'Existing infrastructure & utility asset location',
      'Natural feature profiling (trees, rocks, water bodies)'
    ],
    deliverables: ['2D Contour Maps (0.25m - 1.0m intervals)', '2D CAD Surface Layout (DWG/DXF)', 'CSV Elevation Point Cloud', 'Hardcopy Certified Survey Plans'],
    instruments: 'Leica TS07/TS09 Total Stations, High-accuracy Digital Auto Levels'
  },
  {
    id: 'cadd',
    icon: FileCode2,
    image: '/survey_cad.png',
    title: 'Digital Land Surveying & CAD Consulting',
    category: 'CAD & Consulting',
    shortDesc: 'Paper-to-CAD conversion, raster-to-vector tracing, parcel boundary mapping, and utility coordination drafting.',
    longDesc: 'Transform raw survey data and historical paper drawings into CAD files. We produce clean, layered DWG/DXF drawings adhering strictly to municipal and engineering standards.',
    features: [
      'Paper-to-CAD & Raster-to-Vector digitization',
      'Clean multi-layered CAD drafting standards',
      'Utility coordination drawings (M&E / MEP)',
      'Topographic contour & surface layout creation',
      'As-built drawing preparation for construction'
    ],
    deliverables: ['AutoCAD DWG / DXF Native Files', 'Bentley MicroStation DGN Files', 'Layered CAD Layout Drawings', 'Plot-Ready PDF Sets'],
    instruments: 'AutoCAD, Bentley MicroStation, Global Mapper'
  },
  {
    id: 'boundary',
    icon: Map,
    image: '/dgps_tripod.jpg',
    title: 'Boundary Survey (Land Details)',
    category: 'Land & Legal Details',
    shortDesc: 'Comprehensive land boundary details, perimeter measurement, land parcel separation, and encroachment analysis.',
    longDesc: 'Precise boundary verification matching physical fence lines and landmarks against official revenue land records. Eliminates boundary disputes for buyers, sellers, and developers.',
    features: [
      'Property perimeter & boundary fence measurement',
      'Reconciliation with revenue & village records',
      'Encroachment & setback distance auditing',
      'Subdivision land parcel boundary layout',
      'Certified boundary survey map drafting'
    ],
    deliverables: ['Revenue-Matched Boundary Map', 'Encroachment Audit Report', 'Property Corner Measurement Table', 'DWG/DXF CAD Drawings'],
    instruments: 'Leica Total Stations, DGPS Receivers, Digital Laser Distance Meters'
  },
  {
    id: 'highways',
    icon: Building2,
    image: '/bridge_road_alignment.jpg',
    title: 'Bridge and Road Alignment Preparation',
    category: 'Infrastructure & Engineering',
    shortDesc: 'We provide accurate bridge and road alignment planning based on detailed site surveys.',
    longDesc: 'We provide accurate bridge and road alignment planning based on detailed site surveys. Specialized infrastructure engineering survey for highway corridors, bridge approaches, bypass road alignment preparation, Land Plan Schedule (LPS), and earthwork volumetric calculations (cut/fill quantities).',
    features: [
      'Land Plan Schedule (LPS) preparation for new bypass road projects & highway acquisition',
      'Highway & bypass corridor centerline alignment preparation',
      'Bridge pier & abutment location setting out',
      'Road L-Section & Cross-Section longitudinal profiling',
      'Earthwork Cut & Fill volumetric calculations',
      'Right-of-Way (RoW) boundary demarcation'
    ],
    deliverables: ['Land Plan Schedule (LPS) Acquisition Report', 'Highway Alignment L-Section & Cross-Section Drawings', 'Volumetric Cut & Fill Quantity Calculation Reports', 'Bridge Sub-Structure Survey Sheets', 'DGPS Geo-referenced RoW Plans'],
    instruments: 'FOIF A90 RTK DGPS, Leica Total Station, Digital Auto Level, Civil CAD Suite'
  }
];

const Services = ({ onOpenContact }) => {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <section id="services" className="section-padding" style={{ position: 'relative', background: '#ffffff' }}>
      <div className="container">
        {/* Section Header */}
        <div className="section-title-wrapper">
          <div className="section-subtitle">Core Specialties</div>
          <h2 className="section-title">
            Digital Land Surveying & <span className="gradient-text">Consulting Services</span>
          </h2>
          <p className="section-desc">
            Combining DGPS precision, Leica total stations, and Water Resources Development (W.R.D) expertise for projects across Tamil Nadu.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid-3">
          {servicesData.map((service) => {
            const IconComponent = service.icon;
            return (
              <div
                key={service.id}
                className="glass-card glass-card-interactive"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative',
                  overflow: 'hidden',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  borderRadius: '16px',
                }}
                onClick={() => setSelectedService(service)}
              >
                {/* Image Banner Header */}
                <div style={{ height: '170px', position: 'relative', overflow: 'hidden' }}>
                  <img
                    src={service.image}
                    alt={service.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.4s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to top, rgba(15, 23, 42, 0.6) 0%, transparent 60%)',
                    }}
                  />
                  {/* Category Pill Overlay */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      left: '1rem',
                      padding: '0.25rem 0.75rem',
                      borderRadius: 'var(--radius-full)',
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(8px)',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      color: '#e63946',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  >
                    {service.category}
                  </div>
                </div>

                <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    {/* Icon & Title */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div
                        style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '10px',
                          background: '#fff5f5',
                          border: '1px solid #fed7d7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#e63946',
                          flexShrink: 0,
                        }}
                      >
                        <IconComponent size={22} />
                      </div>
                      <h3 style={{ fontSize: '1.15rem', lineHeight: 1.3, color: '#0f172a' }}>{service.title}</h3>
                    </div>

                    {/* Short Description */}
                    <p style={{ color: '#475569', fontSize: '0.9rem', marginBottom: '1.25rem', lineHeight: 1.55 }}>
                      {service.shortDesc}
                    </p>
                  </div>

                  {/* Card Action */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingTop: '0.85rem',
                      borderTop: '1px solid #e2e8f0',
                      color: '#e63946',
                      fontWeight: '700',
                      fontSize: '0.875rem',
                    }}
                  >
                    <span>Explore Details & Deliverables</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Expanded Service Detail Modal */}
      {selectedService && (
        <div className="modal-backdrop" onClick={() => setSelectedService(null)}>
          <div
            className="glass-card"
            style={{
              maxWidth: '750px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '2.5rem',
              position: 'relative',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Close Button */}
            <button
              onClick={() => setSelectedService(null)}
              style={{
                position: 'absolute',
                top: '1.5rem',
                right: '1.5rem',
                background: '#f1f5f9',
                border: '1px solid #cbd5e1',
                color: '#0f172a',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
              }}
            >
              <X size={20} />
            </button>

            {/* Modal Image Banner */}
            <div style={{ height: '200px', borderRadius: '16px', overflow: 'hidden', marginBottom: '1.5rem', position: 'relative' }}>
              <img src={selectedService.image} alt={selectedService.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(15,23,42,0.7) 0%, transparent 60%)' }} />
              <div style={{ position: 'absolute', bottom: '1.25rem', left: '1.25rem', color: '#ffffff' }}>
                <span className="badge badge-red" style={{ marginBottom: '0.4rem' }}>{selectedService.category}</span>
                <h2 style={{ fontSize: '1.65rem', color: '#ffffff' }}>{selectedService.title}</h2>
              </div>
            </div>

            {/* Detailed Description */}
            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.6, marginBottom: '2.0rem' }}>
              {selectedService.longDesc}
            </p>

            {/* Key Capabilities */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#0f172a' }}>Key Capabilities & Features:</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem' }}>
                {selectedService.features.map((feature, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.95rem', color: '#334155' }}>
                    <Check size={18} color="#e63946" style={{ marginTop: '0.2rem', flexShrink: 0 }} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Deliverables & Instruments */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2.5rem' }}>
              <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h5 style={{ color: '#0066ff', marginBottom: '0.75rem', fontSize: '0.95rem' }}>Client Deliverables:</h5>
                <ul style={{ paddingLeft: '1.2rem', margin: 0, color: '#475569', fontSize: '0.875rem' }}>
                  {selectedService.deliverables.map((item, i) => (
                    <li key={i} style={{ marginBottom: '0.4rem' }}>{item}</li>
                  ))}
                </ul>
              </div>

              <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h5 style={{ color: '#d97706', marginBottom: '0.75rem', fontSize: '0.95rem' }}>Hardware & Tech Used:</h5>
                <p style={{ color: '#475569', fontSize: '0.875rem', lineHeight: 1.5, margin: 0 }}>
                  {selectedService.instruments}
                </p>
              </div>
            </div>

            {/* Modal CTAs */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem' }}>
              <button onClick={() => setSelectedService(null)} className="btn-secondary">
                Close Details
              </button>
              <button
                onClick={() => {
                  setSelectedService(null);
                  onOpenContact(selectedService.title);
                }}
                className="btn-primary"
              >
                Book This Survey Service <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Services;
